import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { resources } from '../../core/services/resources';
import type { Brand } from '../../core/types/data-models';

interface BrandTreeSelectProps {
  value: number | null | undefined;
  onChange: (id: number | null) => void;
  label?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  /**
   * Restrict the option set to a single organization. Omit for the full
   * cross-org brand catalog.
   */
  organizationId?: number | null;
  /**
   * Brand id to omit along with its entire subtree — used when picking
   * a parent so a brand can't become a descendant of itself.
   */
  excludeSubtreeOf?: number;
}

interface FlatBrand {
  brand: Brand;
  depth: number;
}

function buildTree(brands: Brand[], excludeRoot?: number): FlatBrand[] {
  // Index children by parent id so each pass is O(1) instead of O(n).
  const byParent = new Map<number | null, Brand[]>();
  for (const b of brands) {
    const key = b.parentBrandId ?? null;
    const bucket = byParent.get(key);
    if (bucket) bucket.push(b);
    else byParent.set(key, [b]);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }

  // Brands whose parent isn't in the loaded set are surfaced as roots so
  // they don't disappear (e.g. paged out of the 200-row window).
  const knownIds = new Set(brands.map((b) => b.id));
  const orphanRoots = brands
    .filter((b) => b.parentBrandId != null && !knownIds.has(b.parentBrandId))
    .sort((a, b) => a.name.localeCompare(b.name));

  const result: FlatBrand[] = [];
  const visit = (brand: Brand, depth: number) => {
    if (brand.id === excludeRoot) return; // also skips the whole subtree
    result.push({ brand, depth });
    for (const child of byParent.get(brand.id) ?? []) visit(child, depth + 1);
  };
  for (const root of byParent.get(null) ?? []) visit(root, 0);
  for (const orphan of orphanRoots) visit(orphan, 0);
  return result;
}

/**
 * Autocomplete for selecting a brand from the hierarchical brand catalog.
 * Options are flattened in DFS order with leading indentation so the
 * parent → child relationship is visible while typing still works for
 * filtering (the `getOptionLabel` stays plain so the search matches on
 * the bare brand name).
 */
export function BrandTreeSelect({
  value,
  onChange,
  label = 'Parent brand',
  helperText,
  required,
  disabled,
  organizationId,
  excludeSubtreeOf,
}: BrandTreeSelectProps) {
  // Cache key includes the org filter so each filter slice is its own
  // cache entry. Shared with other readers (e.g. table name lookups).
  const list = useQuery({
    queryKey: ['brands', 'select', organizationId ?? 'all'],
    queryFn: () =>
      resources.brands.list({
        pageSize: 200,
        sort: 'name',
        filter: organizationId != null ? { organizationId } : undefined,
      }),
  });

  const flat = useMemo(
    () => buildTree(list.data?.data ?? [], excludeSubtreeOf),
    [list.data, excludeSubtreeOf],
  );

  const selected = flat.find((f) => f.brand.id === value) ?? null;

  return (
    <Autocomplete<FlatBrand, false, false, false>
      fullWidth
      disabled={disabled}
      options={flat}
      value={selected}
      onChange={(_, next) => onChange(next?.brand.id ?? null)}
      // Keep the label plain so the selected-value display and the filter
      // input both use the bare brand name.
      getOptionLabel={(o) => o.brand.name}
      isOptionEqualToValue={(a, b) => a.brand.id === b.brand.id}
      loading={list.isLoading}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          key={option.brand.id}
          sx={{ pl: 2 + option.depth * 2 }}
        >
          {option.depth > 0 && (
            <Typography component="span" sx={{ color: 'text.disabled', mr: 1 }}>
              {'└─'}
            </Typography>
          )}
          <Typography variant="body2">{option.brand.name}</Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {list.isLoading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
